import React from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { FoodDetailItem } from '../../../../../interfaces/types';
import ItemCounter from '../../../../Menu/components/ItemCouter';
import { formatPriceItem } from '../../../../../utils';
import { ReactComponent as NoteIcon } from '../../../../../assets/icon/note.svg';
import { useTranslation } from 'react-i18next';
import { PreOrderDisabledResult } from '../../../../../utils/preOrderDisabled';

interface FoodItemProps {
  food: FoodDetailItem;
  categoryId: string;
  hasFoodInCart: boolean;
  quantity: number;
  restaurantName: string;
  onFoodClick: (food: FoodDetailItem, categoryId: string) => void;
  onSetCount: (newCount: number, itemId: string, categoryId: string) => void;
  onDeleteClick?: (foodId: string, categoryId: string) => void;
  preOrderStatus?: PreOrderDisabledResult;
}

const FoodItem: React.FC<FoodItemProps> = ({
  food,
  categoryId,
  hasFoodInCart,
  quantity,
  restaurantName,
  onFoodClick,
  onSetCount,
  onDeleteClick,
  preOrderStatus,
}) => {
  const { t } = useTranslation();
  const isDisabled = preOrderStatus?.disabled === true;

  return (
    <div
      className="py-4 border-b border-gray-200 cursor-pointer"
      onClick={() => !isDisabled && onFoodClick(food, categoryId)}
      style={isDisabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
    >
      <div className="w-full flex items-end justify-between gap-2 relative">
        <div className="flex items-center flex-grow">
          <div className="relative flex-none">
            <img
              alt={food.name}
              className="rounded-lg w-[100px] h-[100px] object-cover"
              src={food.image_url}
            />
          </div>
          <div className="flex flex-col gap-1 overflow-hidden ml-4 max-w-[72%]">
            <p className="max-h-[3em] overflow-hidden text-ellipsis font-medium">{food.name}</p>
            <p
              style={{
                overflow: 'hidden',
                color: 'var(--Gray-500, #667085)',
                textOverflow: 'ellipsis',
                fontFamily: '"Be Vietnam Pro"',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '18px',
                whiteSpace: 'nowrap',
              }}
            >
              {food.sold !== undefined && food.sold > 0 ? `${food.sold} ${t('SOLD') || 'sold'}` : ''}
              {food.sold !== undefined && food.sold > 0 && food.restaurantId ? ' | ' : ''}
              {food.restaurantId ? restaurantName : ''}
            </p>
            {food.description && (
              <p
                style={{
                  overflow: 'hidden',
                  color: 'var(--Gray-500, #667085)',
                  textOverflow: 'ellipsis',
                  fontFamily: '"Be Vietnam Pro"',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '18px',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {food.description}
              </p>
            )}
            <p className="text-green-500 font-medium">¥ {formatPriceItem(food.price)}</p>
          </div>
        </div>

        {!isDisabled && hasFoodInCart ? (
          <div className="flex w-auto gap-3 items-center justify-end flex-shrink-0 absolute right-0">
            {food.enabled_note && (
              <button className="w-auto flex gap-1 rounded-lg py-1 px-2 border border-neutral-300 items-center h-[24px]">
                <NoteIcon /> <p className="text-black-700 font-semibold text-xs leading-[18px]">{t('MENU_BUTTON_NOTE')}</p>
              </button>
            )}
            <div onClick={(e) => e.stopPropagation()}>
              <ItemCounter
                count={quantity}
                setCount={(newCount) => onSetCount(newCount, food.id, categoryId)}
                initialCountItem={0}
                onDelete={onDeleteClick ? () => onDeleteClick(food.id, categoryId) : undefined}
              />
            </div>
          </div>
        ) : !isDisabled ? (
          <button
            className="w-7 h-7 bg-green-500 rounded-md border border-green-600 flex items-center justify-center flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onFoodClick(food, categoryId);
            }}
          >
            <AddOutlinedIcon className="text-white" style={{ fontSize: 20 }} />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default FoodItem;
